import React, { useState, useRef } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  TextField, 
  ToggleButton,
  ToggleButtonGroup,
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  Divider, 
  Avatar,
  styled,
  Snackbar,
  Alert
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublicIcon from '@mui/icons-material/Public';

// Import custom components
import PostTypeSelector from './post-type-selector';
import PostDisplay from './post-display';
import { applyFormatToText } from './unicodeMappings';
import { svgLike, svgComment, svgRepost, svgSend } from './svgIcons';

// Styled components
const FormattingContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledTextarea = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontFamily: 'monospace',
  }
}));

const SocialActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
}));

const LinkedInTextFormatter = () => {
  const [text, setText] = useState('');
  const [formats, setFormats] = useState([]);
  const [postType, setPostType] = useState('text-only');
  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const textareaRef = useRef(null);

  // Handle text input change
  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  // Handle format change using ToggleButtonGroup
  const handleFormatChange = (event, newFormats) => {
    setFormats(newFormats);
  };
  
  // Apply formatting to selected text
  const applyFormatting = (formatType) => {
    const textarea = textareaRef.current.querySelector('textarea');
    
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Only proceed if there's a selection
    if (start === end) {
      // Show a message instructing to select text first
      return;
    }
    
    const selectedText = text.substring(start, end);
    const formattedSelection = applyFormatToText(selectedText, formatType);
    
    // Replace the selected text with the formatted version
    const newText = text.substring(0, start) + formattedSelection + text.substring(end);
    setText(newText);
    
    // Reset focus to the textarea after the state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedSelection.length);
    }, 0);
  };

  // Copy formatted text to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    setCopiedSnackbarOpen(true);
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setCopiedSnackbarOpen(false);
  };

  // Handle post type change
  const handlePostTypeChange = (event) => {
    setPostType(event.target.value);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
        <strong>Free LinkedIn Text Formatter</strong>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: -0.5 }}>
          Format your LinkedIn post with bold, italic, underline, strikethrough, bullet list and more.
        </Typography>
      </Typography>
      
      <Grid container spacing={3}>
        {/* Editor Panel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            {/* Text formatting controls */}
            <FormattingContainer>
              <ToggleButtonGroup
                value={[]}
                aria-label="text formatting"
              >
                <ToggleButton 
                  value="bold" 
                  aria-label="bold"
                  onClick={() => applyFormatting('bold')}
                >
                  <FormatBoldIcon />
                </ToggleButton>
                <ToggleButton 
                  value="italic" 
                  aria-label="italic"
                  onClick={() => applyFormatting('italic')}
                >
                  <FormatItalicIcon />
                </ToggleButton>
                <ToggleButton 
                  value="boldItalic" 
                  aria-label="bold italic"
                  onClick={() => applyFormatting('boldItalic')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormatBoldIcon />
                    <FormatItalicIcon sx={{ ml: -0.5 }} />
                  </Box>
                </ToggleButton>
                <ToggleButton 
                  value="underline" 
                  aria-label="underline"
                  onClick={() => applyFormatting('underline')}
                >
                  <FormatUnderlinedIcon />
                </ToggleButton>
                <ToggleButton 
                  value="strikethrough" 
                  aria-label="strikethrough"
                  onClick={() => applyFormatting('strikethrough')}
                >
                  <StrikethroughSIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              
              <ToggleButtonGroup
                value={[]}
                aria-label="list formatting"
                sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
              >
                <ToggleButton 
                  value="bulletList" 
                  aria-label="bullet list"
                  onClick={() => applyFormatting('bulletList')}
                >
                  <FormatListBulletedIcon />
                </ToggleButton>
                <ToggleButton 
                  value="numberedList" 
                  aria-label="numbered list"
                  onClick={() => applyFormatting('numberedList')}
                >
                  <FormatListNumberedIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </FormattingContainer>

            {/* Text input */}
            <StyledTextarea
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              placeholder="Type or paste your text here..."
              value={text}
              onChange={handleTextChange}
              ref={textareaRef}
              sx={{ fontFamily: 'monospace' }}
              spellCheck="false"
              autoCorrect="off"
              autoCapitalize="off"
            />
            
            {/* Text area copy button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={copyToClipboard}
              >
                Copy Text
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Preview Panel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            {/* Post Type Selector */}
            <PostTypeSelector 
              postType={postType}
              handlePostTypeChange={handlePostTypeChange}
            />
            
            <Card variant="outlined">
              <CardHeader
                avatar={<Avatar sx={{ width: 48, height: 48 }}>AK</Avatar>}
                title={
                  <Box>
                    <Typography variant="subtitle1" component="span" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                      Anita Kingston
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      • 3rd+
                    </Typography>
                  </Box>
                }
                subheader={
                  postType === 'promoted' ? (
                    <Box sx={{ mt: -0.8, lineHeight: 1.2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        Promoted
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: -0.2 }}>
                        <Typography variant="caption" color="text.secondary">4h</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                        <PublicIcon fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: -0.8, lineHeight: 1.2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Marketing at MakeMyBrand.ai
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: -0.2 }}>
                        <Typography variant="caption" color="text.secondary">4h</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>•</Typography>
                        <PublicIcon fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </Box>
                    </Box>
                  )
                }
              />
              <CardContent>
                {/* Post Display Component */}
                <PostDisplay text={text} postType={postType} />

                {/* Post engagement metrics */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f2f2f2', borderRadius: 5, px: 1, py: 0.5 }}>
                      <Box 
                        component="span" 
                        dangerouslySetInnerHTML={{ __html: svgLike }} 
                        sx={{ mr: 0.5, width: 16, height: 16, display: 'flex' }} 
                      />
                      <Typography variant="body2" color="text.secondary">100</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" align="right">56 comments • 4 reposts</Typography>
                </Box>
              </CardContent>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                <SocialActionButton>
                  <Box 
                    component="span" 
                    dangerouslySetInnerHTML={{ __html: svgLike }} 
                    sx={{ mr: 1, width: 20, height: 20, display: 'flex' }} 
                  />
                  Like
                </SocialActionButton>
                <SocialActionButton>
                  <Box 
                    component="span" 
                    dangerouslySetInnerHTML={{ __html: svgComment }} 
                    sx={{ mr: 1, width: 20, height: 20, display: 'flex' }} 
                  />
                  Comment
                </SocialActionButton>
                <SocialActionButton>
                  <Box 
                    component="span" 
                    dangerouslySetInnerHTML={{ __html: svgRepost }} 
                    sx={{ mr: 1, width: 20, height: 20, display: 'flex' }} 
                  />
                  Repost
                </SocialActionButton>
                <SocialActionButton>
                  <Box 
                    component="span" 
                    dangerouslySetInnerHTML={{ __html: svgSend }} 
                    sx={{ mr: 1, width: 20, height: 20, display: 'flex' }} 
                  />
                  Send
                </SocialActionButton>
              </Box>
            </Card>
          </Paper>
        </Grid>
      </Grid>

      {/* Copy notification */}
      <Snackbar 
        open={copiedSnackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>

      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "3rem auto",
        textAlign: "left",
      }}>
        <strong>Why Use LinkedIn Text Formatter?</strong>
        <ul style={{
          marginTop: 0,
        }}>
          <li>Learn how to format text in LinkedIn posts to boost engagement</li>
          <li>Easily apply bold and italic in LinkedIn for highlighting text</li>
          <li>Create structured, professional-looking content with bullet points and numbered lists</li>
          <li>Preview how many characters before see more appears in your LinkedIn post</li>
        </ul>
      </div>
    </Box>
  );
};

export default LinkedInTextFormatter;